import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch } from 'react-redux';
import { setLoader, setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { postDataAPI } from '../../utils/fetchData';
import axios from 'axios';
import { BASE_URL } from '../../utils/axios';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const DataUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  const dataType = location.state?.type || 'booking'; // 'booking' or 'label'
  const acceptedTypes = dataType === 'booking' 
    ? '.csv,.xlsx,.xls,.pdf'
    : '.csv,.xlsx,.xls,.pdf';

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadedFile(null);

    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const formData = new FormData();
      formData.append('file', file);
      formData.append('dataType', dataType);
      formData.append('fileName', file.name);
      formData.append('fileType', file.type || file.name.split('.').pop());
      formData.append('fileSize', file.size.toString());

      let response = null;
      let fileData = null;

      // Try to upload file to backend
      try {
        response = await axios.post(
          `${BASE_URL}/user/upload-data-file`,
          formData,
          {
            headers: {
              'Authorization': token,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } catch (apiError) {
        // If API endpoint doesn't exist yet, continue with local storage
        console.warn('Upload API not available, storing locally:', apiError);
        response = { data: { status: 200, data: {} } };
      }

      if (response && response.data.status === 200) {
        const fileData = {
          fileName: file.name,
          name: file.name,
          dataType: dataType,
          type: dataType,
          fileSize: file.size,
          size: file.size,
          fileType: file.type || file.name.split('.').pop(),
          uploadDate: new Date().toISOString(),
          fileId: response.data.data?.fileId || `file_${Date.now()}`,
          status: 'uploaded',
          ...response.data.data
        };
        
        setUploadedFile(fileData);
        
        // Track the uploaded file using utility
        try {
          const { trackUploadedFile } = require('../../utils/uploadedFilesTracker');
          trackUploadedFile(file, dataType, {
            fileId: fileData.fileId,
            status: 'uploaded',
            source: 'data-upload'
          });
        } catch (e) {
          console.error('Error tracking file:', e);
        }
        
        dispatch(setSuccessAlert(`${file.name} uploaded successfully`));
        
        // For booking files, navigate back to data view
        if (dataType === 'booking' && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
          // Navigate back to connectors data view
          setTimeout(() => {
            navigate('/connectors/data-view', {
              state: {
                category: 'bookings',
                uploadedFile: fileData,
                fileType: file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? 'Excel' : 'CSV',
                fromDataUpload: true
              }
            });
          }, 1000);
        } else if (dataType === 'label') {
          // For label files, process and connect to Money/Catalog/Analytics apps
          // TODO: Implement label file processing when backend is ready
          // For now, just navigate to data view
          setTimeout(() => {
            navigate('/connectors/data-view', {
              state: {
                category: 'label'
              }
            });
          }, 1000);
        } else {
          // For other files (PDFs, etc.), navigate to data view
          setTimeout(() => {
            navigate('/connectors/data-view', {
              state: {
                category: dataType === 'booking' ? 'bookings' : 'label'
              }
            });
          }, 1000);
        }
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      dispatch(setErrorAlert(error.response?.data?.message || 'Failed to upload file. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>
              Upload {dataType === 'booking' ? 'Booking' : 'Label'} Data
            </span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate(-1)}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {!uploadedFile ? (
              <div className="flex flex-col">
                <div className="mb-6">
                  <p className="text-white text-[12px] opacity-70 leading-relaxed mb-4">
                    {dataType === 'booking' 
                      ? 'Upload show advances, deal sheets, and settlement PDFs. Supported formats: CSV, Excel, PDF.'
                      : 'Upload label, distributor, and DSP royalty reports and statements. Supported formats: CSV, Excel, PDF.'
                    }
                  </p>
                </div>

                <div 
                  className="bg-black border-2 border-dashed border-[rgba(255,255,255,0.2)] rounded-lg p-8 hover:border-[#FF2300] transition-all cursor-pointer text-center"
                  onClick={handleFileSelect}
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-[rgba(4,205,208,0.2)] rounded-lg flex items-center justify-center">
                      <svg className="w-8 h-8 text-[#FF2300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white text-[14px] font-semibold mb-2">
                        {uploading ? 'Uploading...' : 'Select File to Upload'}
                      </h3>
                      <p className="text-white text-[11px] opacity-70">
                        {uploading ? 'Please wait while we upload your file' : 'Click here or drag and drop'}
                      </p>
                      <p className="text-white text-[10px] opacity-50 mt-2">
                        {acceptedTypes.replace(/\./g, '').toUpperCase()}
                      </p>
                    </div>
                    {uploading && (
                      <div className="w-8 h-8 border-2 border-[#FF2300] border-t-transparent rounded-full animate-spin" />
                    )}
                  </div>
                </div>

                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  accept={acceptedTypes}
                  style={{ display: 'none' }} 
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-green-500 bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-white text-[14px] font-semibold mb-2">Upload Successful!</h3>
                <p className="text-white text-[12px] opacity-70 mb-4">{uploadedFile.name}</p>
                <p className="text-white text-[11px] opacity-50">
                  {dataType === 'booking' 
                    ? 'Processing your booking data...'
                    : 'Your file has been uploaded and will be processed.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default DataUpload;

